import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';

import { type ExperienceEvent, type ExperienceAttributes, hasAttribute } from './ExperienceEvent';
import {
	CREATE_INLINE_COMMENT_EXPERIENCE,
	RESOLVE_INLINE_COMMENT_EXPERIENCE,
	SELECT_INLINE_COMMENT_EXPERIENCE,
} from './ExperienceName';
import { ExperienceTimeout } from './ExperienceTimeout';
import { getExperienceTracker } from './getExperienceTracker';
import { startUFOPressTrace } from './startExperienceForRoute';

export type SelectInlineCommentCompoundExperienceEntryPoint =
	/**
	 * Used when a user creates a new comment by highlighting text and clicking the create comment button.
	 * Triggered when a new inline comment is successfully saved.
	 */
	| 'create-comment'
	/**
	 * Used when a user clicks on an annotation in the document to select an existing comment.
	 * Triggered when annotation selection is completed in the editor.
	 */
	| 'annotation-change'
	/**
	 * Used when a user navigates between comments using the previous/next buttons in the comment sidebar.
	 * Triggered by clicking navigation arrows in the CommentNavigation component.
	 */
	| 'comment-navigation'
	/**
	 * Used when a user navigates between comments using keyboard shortcuts.
	 * Triggered by keyboard shortcut listeners for next/previous comment navigation.
	 */
	| 'keyboard-navigation'
	/**
	 * Used when a user selects a comment from the comments panel.
	 * Default entry point for panel-based comment selections.
	 */
	| 'sidebar'
	/**
	 * Fallback entry point when renderer focuses on an annotation.
	 * Used when a user clicks on an annotation in the document to select an existing comment.
	 * Triggered when annotation selection is completed in the renderer.
	 */
	| 'unknown:RendererFocus'
	/**
	 * Fallback entry point for inline comment selections in the document with unknown trigger.
	 * Used as a generic fallback when the selection mechanism is not explicitly tracked.
	 */
	| 'unknown:InlineComment'
	/**
	 * Fallback entry point for inline comment selections in the comments panel with unknown trigger.
	 * Specifically used when keyboard navigation is followed by comments panel selection.
	 */
	| 'unknown:CommentsPanelList';

type AnnotationIdKnownStartAttributes = {
	pageClass: 'editor' | 'renderer' | 'not-page-or-doc';
	/**
	 * This is optional as in some scenarios the entry point does not know
	 * what type of comment is being selected (e.g. comment navigation, sidebar).
	 **/
	commentType?: 'block' | 'inline';
	blockType?: 'media';
	annotationId: string;
	entryPoint:
		| 'create-comment'
		| 'annotation-change'
		| 'sidebar'
		| 'unknown:RendererFocus'
		| 'unknown:InlineComment'
		| 'unknown:CommentsPanelList';
};
type AnnotationIdUnknownStartAttributes = {
	pageClass: 'editor' | 'renderer' | 'not-page-or-doc';
	/**
	 * This is optional as in some scenarios the entry point does not know
	 * what type of comment is being selected (e.g. comment navigation, sidebar).
	 **/
	commentType?: 'block' | 'inline';
	blockType?: 'media';
	annotationId?: undefined;
	entryPoint: 'keyboard-navigation' | 'comment-navigation';
};
export type StartAttributes = AnnotationIdKnownStartAttributes | AnnotationIdUnknownStartAttributes;

let commonSelectInlineCommentExperiencesAttributes: ExperienceAttributes = {};

function getSelectInlineCommentExperienceDebug(experienceName: string, experienceStage?: string) {
	function createInlineCommentExperienceDebug({
		error,
		createAnalyticsEvent,
		extraAttributes,
	}: {
		createAnalyticsEvent: CreateUIAnalyticsEvent;
		error: Error;
		extraAttributes?: ExperienceAttributes;
	}) {
		const experienceTracker = getExperienceTracker();

		if (fg('cc_comments_debug_select_aborts')) {
			experienceTracker.attachDebugPoint({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				message: `Legacy select debug message: ${error.message || String(error)}`,
				attributes: {
					...commonSelectInlineCommentExperiencesAttributes,
					...extraAttributes,
					experienceStage,
				},
			});

			return;
		}

		const experienceAttributes = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		);
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'error',
				actionSubject: SELECT_INLINE_COMMENT_EXPERIENCE,
				source: experienceName,
				attributes: {
					experienceStage,
					...experienceAttributes,
					...commonSelectInlineCommentExperiencesAttributes,
					...extraAttributes,

					error: error.message || String(error),
					stack: error.stack,
				},
			},
		}).fire();
	}
	return createInlineCommentExperienceDebug;
}

enum ExperienceType {
	DOCUMENT = 'document',
	DOCUMENT_WITH_PANEL = 'documentWithPanel',
	PANEL = 'panel',
}

function getExperienceType(
	entryPoint: SelectInlineCommentCompoundExperienceEntryPoint,
	sidebarOpenAtStart: boolean,
): ExperienceType {
	switch (entryPoint) {
		case 'create-comment':
		case 'comment-navigation':
		case 'unknown:InlineComment':
			return ExperienceType.DOCUMENT;
		case 'annotation-change':
		case 'keyboard-navigation':
		case 'unknown:RendererFocus':
			return sidebarOpenAtStart ? ExperienceType.DOCUMENT_WITH_PANEL : ExperienceType.DOCUMENT;
		case 'sidebar':
		case 'unknown:CommentsPanelList':
			return ExperienceType.PANEL;
		default:
			return ExperienceType.DOCUMENT;
	}
}

export const selectInlineCommentCompoundExperience = {
	_start: (startAttributes: StartAttributes, _options?: { annotationUnknown: true }) => {
		if (startAttributes.pageClass === 'not-page-or-doc') {
			return;
		}

		const experienceTracker = getExperienceTracker();
		const experienceState = experienceTracker.getExperienceState(SELECT_INLINE_COMMENT_EXPERIENCE);
		const runningExperienceAttributes = experienceState?.attributes;

		if (
			!experienceState?.hasStopped &&
			runningExperienceAttributes?.annotationId !== undefined &&
			runningExperienceAttributes?.annotationId === startAttributes.annotationId
		) {
			// If there is an existing (unstopped) experience
			// - and that experience has an annotationId
			//   - keyboard and comment navigation will start experiences without an annotationId -- and these should be restarted on an new start
			// - and that annotationId matches the one in the startAttributes
			// we assume the experience is still active, and don't start a new one.
			// the reason we can hit this is because the selectInlineCommentCompoundExperience._start is called when there is a "select inline comment"
			// signal, and individual signals fire in different orders (sometimes they are the first signal, sometimes they are later).

			// However -- if the second signal has a clearer entry point
			// we update the entry point attribute of the existing experience.
			if (
				(runningExperienceAttributes?.entryPoint as string | undefined)?.startsWith('unknown') &&
				!startAttributes.entryPoint.startsWith('unknown')
			) {
				selectInlineCommentCompoundExperience.addCommonAttributes({
					entryPoint: startAttributes.entryPoint,
				});
				if (fg('cc_comments_debug_select_aborts')) {
					experienceTracker.attachDebugPoint({
						name: SELECT_INLINE_COMMENT_EXPERIENCE,
						message: `After starting from ${runningExperienceAttributes.entryPoint}, received signal from ${startAttributes.entryPoint}`,
					});
				}
			}

			return;
		}

		if (
			// When the side bar is open, and the user selects a comment using the keyboard,
			// the keyboard navigation triggers a select annotation first (without an annotationId),
			// followed by the show comment start or select annotation complete (with an annotationId.
			startAttributes.entryPoint === 'unknown:CommentsPanelList' &&
			runningExperienceAttributes?.entryPoint === 'keyboard-navigation' &&
			runningExperienceAttributes?.annotationId === undefined
		) {
			selectInlineCommentCompoundExperience.addCommonAttributes({
				annotationId: startAttributes.annotationId,
			});

			if (fg('cc_comments_debug_select_aborts')) {
				experienceTracker.attachDebugPoint({
					name: SELECT_INLINE_COMMENT_EXPERIENCE,
					message: `After starting from ${runningExperienceAttributes.entryPoint}, received signal from ${startAttributes.entryPoint} with annotationId ${startAttributes.annotationId}`,
				});
			}

			return;
		}

		// First prepare the new attributes without modifying the global state
		// This ensures that any abort of current experiences will use their original attributes
		const newAttributes: ExperienceAttributes = {
			pageClass: startAttributes.pageClass,
			commentType: startAttributes.commentType,
			annotationId: 'annotationId' in startAttributes ? startAttributes.annotationId : undefined,
			entryPoint: startAttributes.entryPoint,
			version: 'single-experience',
		};

		// We check if there is a dom element with a testId of "object-sidebar-panel-body" and if so mark the
		// experience as being during a sidebar open experience.

		newAttributes.sidebarOpenAtStart =
			document.querySelector('[data-testid="object-sidebar-panel-body"]') !== null;

		newAttributes.experienceType = getExperienceType(
			startAttributes.entryPoint,
			newAttributes.sidebarOpenAtStart as boolean,
		);

		// If the create inline comment experience is running, we want to link the two experiences
		const createInlineCommentExperienceState = experienceTracker.getExperienceState(
			CREATE_INLINE_COMMENT_EXPERIENCE,
		);

		if (createInlineCommentExperienceState && !createInlineCommentExperienceState.hasStopped) {
			newAttributes.linkedCompoundTaskId =
				createInlineCommentExperienceState.attributes?.compoundTaskId;
		}

		// Adds a debug point to the last running experience (debug points are only added if there is a running experience)
		// Note:
		// A new experience is followed after this is fired -- so this should always be the last debug point (as the new experience will trigger a fail or abort).
		// In the case there is no running experience, the experience tracker will discard the debugPoint.
		experienceTracker.attachDebugPoint({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			message: `Restarted from ${startAttributes.entryPoint} with annotationId: ${startAttributes.annotationId} (sidebarOpenAtStart: ${newAttributes.sidebarOpenAtStart})`,
		});
		// Start press trace if no trace is currently active
		startUFOPressTrace(SELECT_INLINE_COMMENT_EXPERIENCE);

		// Then start a new compound experience with the new attributes
		// This will abort any running experiences with the same name, but they'll keep their original attributes
		experienceTracker.start({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			attributes: newAttributes,
			// If the annotation is not selected and comment shown to the user within 12 seconds -- we assume there has been a failure to select the inline comment
			timeout: ExperienceTimeout.SELECT_INLINE_COMMMENT,

			collect: (events) => {
				const isThisCommentBeingResolved = (event: ExperienceEvent) => {
					return (
						event.name === RESOLVE_INLINE_COMMENT_EXPERIENCE &&
						event.action === 'taskStart' &&
						hasAttribute(
							event,
							'annotationId',
							commonSelectInlineCommentExperiencesAttributes.annotationId,
						)
					);
				};
				events.forEach((event) => {
					if (isThisCommentBeingResolved(event)) {
						selectInlineCommentCompoundExperience.abort({
							abortReason: 'Comment being resolved',
						});
					}
				});
			},
		});

		if (startAttributes.annotationId === undefined) {
			experienceTracker.attachDebugPoint({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				message: `selectInlineCommentCompoundExperience._start started from ${startAttributes.entryPoint} with unknown annotationId`,
			});
		} else {
			if (fg('cc_comments_debug_select_aborts')) {
				experienceTracker.attachDebugPoint({
					name: SELECT_INLINE_COMMENT_EXPERIENCE,
					message: `selectInlineCommentCompoundExperience._start started from ${startAttributes.entryPoint} with annotationId ${startAttributes.annotationId}`,
				});
			}
		}

		// Now it's safe to update the global state since any aborts have already happened
		// We reset experience state if we are starting a new experience
		commonSelectInlineCommentExperiencesAttributes = newAttributes;
	},
	debugPoint: (message: string, attributes?: ExperienceAttributes) => {
		const experienceTracker = getExperienceTracker();
		experienceTracker.attachDebugPoint({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			message,
			attributes,
		});
	},
	addCommonAttributes: (additionalCommonAttributes: {
		[key: string]: boolean | null | number | string | undefined | Array<number | string>;
	}) => {
		const experienceTracker = getExperienceTracker();
		commonSelectInlineCommentExperiencesAttributes = {
			...commonSelectInlineCommentExperiencesAttributes,
			...additionalCommonAttributes,
		};

		experienceTracker.updateAttributes({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			attributes: additionalCommonAttributes,
		});
	},
	abort: ({
		abortReason,
	}: {
		abortReason:
			| 'test abort'
			| 'Comment being resolved'
			| 'Selected comment which has been resolved';
	}) => {
		const experienceTracker = getExperienceTracker();
		experienceTracker.abort({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			reason: abortReason,
			checkForTimeout: true,
		});
		commonSelectInlineCommentExperiencesAttributes = {};
	},
	selectAnnotation: {
		debug: getSelectInlineCommentExperienceDebug(
			SELECT_INLINE_COMMENT_EXPERIENCE,
			'selectAnnotation',
		),
		/**
		 * This is currently used to start from
		 * - annotationId unknown
		 *   - comment navigation
		 *   - keyboard navigation
		 * - annotationId known
		 *   - create comment
		 *   - comment sidebar comment selection
		 *
		 * These should be separate signals once the multi experience is deprecated.
		 */
		start: (
			startAttributes: AnnotationIdKnownStartAttributes | AnnotationIdUnknownStartAttributes,
		) => {
			function isUnknownAnnotationId(
				startAttributes: StartAttributes,
			): startAttributes is AnnotationIdKnownStartAttributes {
				return (
					startAttributes.entryPoint === 'keyboard-navigation' ||
					startAttributes.entryPoint === 'comment-navigation'
				);
			}

			if (isUnknownAnnotationId(startAttributes)) {
				selectInlineCommentCompoundExperience._start(startAttributes, {
					annotationUnknown: true,
					// there is no annotationId on the startAttributes for these entry points
				});

				return;
			}

			selectInlineCommentCompoundExperience._start(startAttributes);
		},
		abort: ({
			abortReason,
		}: {
			abortReason:
				| 'Comment navigation when only one comment'
				| 'Navigating to general comment'
				| 'Parent experience aborting with reason: test abort'
				| 'Parent experience aborting with reason: Comment being resolved'
				| 'Parent experience aborting with reason: Selected comment which has been resolved';
		}) => {
			selectInlineCommentCompoundExperience.abort({
				// @ts-ignore the abort reasons have mismatch due to type from the old multi experience. and once that is removed we can remove this cast
				abortReason,
			});
			commonSelectInlineCommentExperiencesAttributes = {};
		},
		/**
		 * Note -- the selectAnnotation does not trigger a completion of the selectInlineComment experience.
		 * Instead -- the selectInlineComment experience is completed when the showComment experience completes
		 * (which looks for the selected annotation as part of its completion logic).
		 *
		 * With the single experience -- this is less a complete and rather just an indirect signal that a selection has been made (in case it's not detected by the showComment experience).
		 * When the multi selectInlineCommentCompoundExperience is deprecated, this can be renamed to indicate it's a "annotationSelection" signal rather than a "completed" signal.
		 */
		complete: (startAttributes: StartAttributes & { annotationId: string }) => {
			const experienceTracker = getExperienceTracker();

			const experienceState = experienceTracker.getExperienceState(
				SELECT_INLINE_COMMENT_EXPERIENCE,
			);

			// There can be cases where no start has been called for the select experience.
			// - When a product consumer of the editor setselectedannotation api calls the select experience
			//   and has not been instrumented.
			// - When the annotation selection is via editor logic (ie. editor selection being moved onto
			//   an annotation).
			if (experienceState === null) {
				experienceTracker.attachDebugPoint({
					name: SELECT_INLINE_COMMENT_EXPERIENCE,
					message: 'selectAnnotation completed from no experience - no explicit start point',
					attributes: startAttributes,
				});

				selectInlineCommentCompoundExperience._start(startAttributes);
			}
		},
	},
	showComment: {
		debug: getSelectInlineCommentExperienceDebug(SELECT_INLINE_COMMENT_EXPERIENCE, 'showComment'),
		debugPoint: (message: string, attributes?: ExperienceAttributes) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.attachDebugPoint({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				message,
				attributes,
			});
		},
		start: (startAttributes: StartAttributes & { annotationId: string }) => {
			const experienceTracker = getExperienceTracker();
			selectInlineCommentCompoundExperience._start(startAttributes);

			experienceTracker.attachDebugPoint({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				message: 'showComment start triggered',
				attributes: startAttributes,
			});
		},
		abort: ({
			abortReason,
		}: {
			abortReason:
				| 'Non inline comment being shown'
				| 'Comment sidebar dismissed'
				| 'InlineComment component unmounted'
				| 'InlineComment ui received new data and is hiding the inline comment ui'
				| 'Parent experience aborting with reason: test abort'
				| 'Parent experience aborting with reason: Comment being resolved'
				| 'Parent experience aborting with reason: Selected comment which has been resolved';
		}) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.abort({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				reason: abortReason,
			});
			commonSelectInlineCommentExperiencesAttributes = {};
		},
		fail: (error: Error, extraAttributes?: { misalignedBy: number }) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.fail({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				error,
				attributes: { ...commonSelectInlineCommentExperiencesAttributes, ...extraAttributes },
			});
			commonSelectInlineCommentExperiencesAttributes = {};
		},
		complete: (completedFrom?: string) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.succeed({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				attributes: { completedFrom, ...commonSelectInlineCommentExperiencesAttributes },
			});
			commonSelectInlineCommentExperiencesAttributes = {};
		},
		standaloneSidebarComplete: (commentUIVerticalDistanceFromAnnotation: number) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.attachDebugPoint({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				message: `standaloneSidebarComplete triggered`,
			});

			if (commentUIVerticalDistanceFromAnnotation > 40) {
				experienceTracker.attachDebugPoint({
					name: SELECT_INLINE_COMMENT_EXPERIENCE,
					message: `Comment UI is misaligned by >40px pixels`,
					attributes: {
						misalignedBy: commentUIVerticalDistanceFromAnnotation,
					},
				});
			}

			if (typeof commonSelectInlineCommentExperiencesAttributes.annotationId !== 'string') {
				experienceTracker.fail({
					name: SELECT_INLINE_COMMENT_EXPERIENCE,
					error: new Error(
						'selectInlineCommentCompoundExperience.standaloneSidebarComplete called when no annotationId was known',
					),
					attributes: commonSelectInlineCommentExperiencesAttributes,
				});
				return;
			}
			if (
				!__checkAnnotationVisibleAndFocused(
					commonSelectInlineCommentExperiencesAttributes.annotationId,
				)
			) {
				experienceTracker.fail({
					name: SELECT_INLINE_COMMENT_EXPERIENCE,
					error: new Error(
						`selectInlineCommentCompoundExperience.standaloneSidebarComplete ended without a focused and in viewport annotation.`,
					),
					attributes: commonSelectInlineCommentExperiencesAttributes,
				});
			}

			experienceTracker.succeed({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				attributes: {
					completedFrom: 'Standalone sidebar inline comment selection completed',
					...commonSelectInlineCommentExperiencesAttributes,
				},
			});
		},
		sidePanelComplete: () => {
			const experienceTracker = getExperienceTracker();

			experienceTracker.attachDebugPoint({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				message: `sidePanelComplete triggered`,
			});

			if (typeof commonSelectInlineCommentExperiencesAttributes.annotationId !== 'string') {
				experienceTracker.fail({
					name: SELECT_INLINE_COMMENT_EXPERIENCE,
					error: new Error(
						'selectInlineCommentCompoundExperience.sidePanelComplete called when no annotationId was known',
					),
					attributes: commonSelectInlineCommentExperiencesAttributes,
				});
				return;
			}
			if (
				!__checkAnnotationVisibleAndFocused(
					commonSelectInlineCommentExperiencesAttributes.annotationId,
				)
			) {
				experienceTracker.fail({
					name: SELECT_INLINE_COMMENT_EXPERIENCE,
					error: new Error(
						'selectInlineCommentCompoundExperience.sidePanelComplete did not result in a visible and focused annotation',
					),
					attributes: commonSelectInlineCommentExperiencesAttributes,
				});
			}

			experienceTracker.succeed({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				attributes: {
					completedFrom: 'Side Panel inline comment selection completed',
					...commonSelectInlineCommentExperiencesAttributes,
				},
			});
		},
	},
};

/**
 *
 * Checks if there is an annotation currently visible in the document.
 *
 * The comment UI is currently opinionated about the comment UI being attached to the first instance of the focused annotation element.
 *
 * So we can safely skip checking if the initial focused annotation is not visible
 *
 * Note: this method causes reflow, and should not be used in performance sensitive code.
 *
 */
function __checkAnnotationVisibleAndFocused(annotationId: string): boolean {
	const experienceTracker = getExperienceTracker();

	const possibleFocusedAnnotationElements = document.querySelectorAll(
		// This will match the annotation id in either the Editor or Renderer
		`[data-id="${annotationId}"][data-mark-annotation-type="inlineComment"][data-has-focus="true"],
		[data-id="${annotationId}"][data-mark-annotation-type="inlineComment"] .ak-editor-annotation-focus`,
	);

	if (possibleFocusedAnnotationElements.length === 0) {
		experienceTracker.attachDebugPoint({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			message: `No annotation element found`,
		});

		return false;
	}

	/**
	 * Annotations can have multiple elements in the dom, with each of the elements having the focus state set to true.
	 * Ie. Comments spanning multiple lines will have multiple elements with the focus state set to true.
	 *
	 * In addition to this -- sometimes there will be "hidden" focused annotation elements where the dom is duplicated
	 * such as for annotations on table header cells in the renderer.
	 *
	 * If the first element is not visible due to being hidden, we continue checking until we reach a visible element, and then check whether it is in the viewport.
	 *
	 */

	for (let i = 0; i < possibleFocusedAnnotationElements.length; i++) {
		const element = possibleFocusedAnnotationElements[i];
		if (element instanceof HTMLElement) {
			if (!element.checkVisibility()) {
				// Skip to the next element if the current one is not visible
				// This can happen in scenarios where the dom is duplicated, such as for annotations on table header cells in the renderer.

				experienceTracker.attachDebugPoint({
					name: SELECT_INLINE_COMMENT_EXPERIENCE,
					message: `Annotation element had a hidden focused element, skipping to next element`,
				});

				continue;
			}

			const rect = element.getBoundingClientRect();

			const isVisible =
				rect.top >= 0 &&
				rect.left >= 0 &&
				rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
				rect.right <= (window.innerWidth || document.documentElement.clientWidth);

			// This is the first visible element -- which should be focused and
			// in the viewport
			if (isVisible) {
				return true;
			} else {
				experienceTracker.attachDebugPoint({
					name: SELECT_INLINE_COMMENT_EXPERIENCE,
					message: `Annotation element found - but not in viewport`,
				});

				return false;
			}
		}
	}

	return false;
}
